import React, { useEffect, useState, createContext, useContext } from 'react';
import { User, UserRole, Region } from '../types/models';
import { getDomainContext } from '../utils/domainAuth';
// Registered user type for user management
interface RegisteredUser {
  id: string;
  name: string;
  surname: string;
  email: string;
  password: string;
  cellNumber?: string;
  createdAt: string;
  hasDefaultPassword: boolean;
  emailMarketingConsent: boolean;
  orderUpdatesConsent: boolean;
  whatsappConsent: boolean;
  region?: Region;
}
// Admin credentials (separate from regular users)
const ADMIN_CREDENTIALS = {
  main: {
    email: 'admin@main.com',
    password: ''
  },
  limpopo: {
    email: 'admin@limpopo.com',
    password: ''
  },
  gauteng: {
    email: 'admin@gauteng.com',
    password: ''
  }
};
interface AuthContextType {
  user: User | null;
  users: RegisteredUser[];
  login: (email: string, password: string) => Promise<User>;
  signup: (name: string, surname: string, email: string, password: string, region: Region, cellNumber?: string, whatsappNumber?: string) => Promise<void>;
  updateProfile: (updates: {
    email?: string;
    cellNumber?: string;
    whatsappNumber?: string;
  }) => Promise<void>;
  logout: () => void;
  resetUserPassword: (userId: string) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<RegisteredUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Check for existing session
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    // Load registered users from localStorage (no seed data)
    const storedUsers = localStorage.getItem('bloom-pop-users');
    if (storedUsers) {
      setUsers(JSON.parse(storedUsers));
    } else {
      // Initialize with empty array - no hard-coded users
      setUsers([]);
      localStorage.setItem('bloom-pop-users', JSON.stringify([]));
    }
    setIsLoading(false);
  }, []);
  const login = async (email: string, password: string): Promise<User> => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    const emailLower = email.toLowerCase().trim();
    // Check if it's an admin login
    const isAdminEmail = emailLower.includes('admin');
    if (isAdminEmail) {
      // Admin auto-login - no password required
      let adminType: 'main' | 'regional' | undefined;
      let region: Region = 'All';
      let isValidAdmin = false;
      if (emailLower === ADMIN_CREDENTIALS.main.email) {
        adminType = 'main';
        region = 'All';
        isValidAdmin = true;
      } else if (emailLower === ADMIN_CREDENTIALS.limpopo.email) {
        adminType = 'regional';
        region = 'Limpopo';
        isValidAdmin = true;
      } else if (emailLower === ADMIN_CREDENTIALS.gauteng.email) {
        adminType = 'regional';
        region = 'Gauteng';
        isValidAdmin = true;
      }
      if (!isValidAdmin) {
        throw new Error('Invalid admin email address');
      }
      const adminUser: User = {
        id: 'admin-' + Math.random().toString(36).substr(2, 9),
        name: 'Admin',
        surname: region === 'All' ? 'Main' : region,
        email: emailLower,
        role: 'admin',
        region,
        adminType,
        avatar: `https://ui-avatars.com/api/?name=Admin+${region}&background=random`
      };
      setUser(adminUser);
      localStorage.setItem('user', JSON.stringify(adminUser));
      return adminUser;
    }
    // Regular user login - requires password validation
    if (!password) {
      throw new Error('Password is required');
    }
    const registeredUser = users.find(u => u.email.toLowerCase() === emailLower);
    if (!registeredUser) {
      throw new Error('No account found with this email. Please sign up first.');
    }
    if (registeredUser.password !== password) {
      throw new Error('Incorrect password. Please try again.');
    }
    // Create user session
    const loggedInUser: User = {
      id: registeredUser.id,
      name: registeredUser.name,
      surname: registeredUser.surname,
      email: registeredUser.email,
      cellNumber: registeredUser.cellNumber,
      whatsappNumber: registeredUser.whatsappNumber || '',
      role: 'user',
      region: registeredUser.region,
      avatar: `https://ui-avatars.com/api/?name=${registeredUser.name}+${registeredUser.surname}&background=random`
    };
    setUser(loggedInUser);
    localStorage.setItem('user', JSON.stringify(loggedInUser));
    return loggedInUser;
  };
  const signup = async (name: string, surname: string, email: string, password: string, region: Region, cellNumber?: string, whatsappNumber?: string) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    const emailLower = email.toLowerCase().trim();
    // Check if email already exists
    const existingUser = users.find(u => u.email.toLowerCase() === emailLower);
    if (existingUser) {
      throw new Error('An account with this email already exists. Please log in instead.');
    }
    // Validate inputs
    if (!name || !surname || !email || !password) {
      throw new Error('All fields are required');
    }
    if (password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }
    // Create new registered user
    const newRegisteredUser: RegisteredUser = {
      id: Math.random().toString(36).substr(2, 9),
      name,
      surname,
      email: emailLower,
      password,
      cellNumber: cellNumber || '',
      createdAt: new Date().toISOString(),
      hasDefaultPassword: false,
      emailMarketingConsent: false,
      orderUpdatesConsent: true,
      whatsappConsent: false,
      region
    };
    // Add to users list and save
    const updatedUsers = [...users, newRegisteredUser];
    setUsers(updatedUsers);
    localStorage.setItem('bloom-pop-users', JSON.stringify(updatedUsers));
    // Auto-login the new user
    const newUser: User = {
      id: newRegisteredUser.id,
      name,
      surname,
      email: emailLower,
      cellNumber: cellNumber || '',
      whatsappNumber: whatsappNumber || '',
      role: 'user',
      region,
      avatar: `https://ui-avatars.com/api/?name=${name}+${surname}&background=random`
    };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };
  const updateProfile = async (updates: {
    email?: string;
    cellNumber?: string;
    whatsappNumber?: string;
  }) => {
    if (!user) throw new Error('No user logged in');
    await new Promise(resolve => setTimeout(resolve, 500));
    const updatedUser = {
      ...user,
      ...updates
    };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    // Also update in users list
    const updatedUsers = users.map(u => u.id === user.id ? {
      ...u,
      email: updates.email || u.email,
      cellNumber: updates.cellNumber || u.cellNumber
    } : u);
    setUsers(updatedUsers);
    localStorage.setItem('bloom-pop-users', JSON.stringify(updatedUsers));
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    window.location.href = '/login';
  };
  const resetUserPassword = (userId: string) => {
    const updatedUsers = users.map(u => u.id === userId ? {
      ...u,
      hasDefaultPassword: true
    } : u);
    setUsers(updatedUsers);
    localStorage.setItem('bloom-pop-users', JSON.stringify(updatedUsers));
    alert('Password reset successfully. User will receive an email with temporary password.');
  };
  return <AuthContext.Provider value={{
    user,
    users,
    login,
    signup,
    updateProfile,
    logout,
    resetUserPassword,
    isLoading,
    isAuthenticated: !!user
  }} data-id="element-1188">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}